import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const LINKS = [
  { label: "Services", id: "services", testid: "nav-link-services" },
  { label: "Process", id: "process", testid: "nav-link-process" },
  { label: "Work", id: "work", testid: "nav-link-work" },
  { label: "Contact", id: "contact", testid: "nav-link-contact" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const go = (id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate(`/#${id}`);
    } else {
      scrollToId(id);
    }
  };

  return (
    <header
      data-testid="nav-header"
      className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#121316]/80 border-b border-zinc-800/60"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-[72px] flex items-center justify-between">
        <Link to="/" data-testid="nav-logo-link" className="flex items-center gap-2.5 group">
          <span className="font-mono text-teal-400 text-lg font-semibold tracking-tight transition-transform duration-300 group-hover:-rotate-6">
            &lt;/&gt;
          </span>
          <span className="font-display font-bold text-lg tracking-tight text-zinc-50">
            BTM<span className="text-teal-400">.</span>
            <span className="hidden sm:inline text-zinc-400 font-medium text-sm ml-2 tracking-normal">
              tech developments
            </span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={l.testid}
              onClick={() => go(l.id)}
              className="text-sm text-zinc-400 hover:text-teal-300 transition-colors duration-300 font-medium"
            >
              {l.label}
            </button>
          ))}
          <Link
            to="/privacy"
            data-testid="nav-link-privacy"
            className="text-sm text-zinc-400 hover:text-teal-300 transition-colors duration-300 font-medium"
          >
            Privacy
          </Link>
          <button
            data-testid="nav-cta-start-project"
            onClick={() => go("contact")}
            className="text-sm font-semibold bg-teal-500 text-[#121316] px-5 py-2.5 rounded-full hover:bg-teal-400 hover:shadow-[0_0_24px_rgba(20,184,166,0.35)] transition-all duration-300"
          >
            Start a Project
          </button>
        </nav>

        <button
          data-testid="mobile-menu-button"
          onClick={() => setOpen(!open)}
          className="md:hidden text-zinc-300 p-2"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden overflow-hidden border-t border-zinc-800/60 bg-[#121316]/95"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  data-testid={`mobile-${l.testid}`}
                  onClick={() => go(l.id)}
                  className="text-left text-base text-zinc-300 hover:text-teal-300 transition-colors"
                >
                  {l.label}
                </button>
              ))}
              <Link
                to="/privacy"
                data-testid="mobile-nav-link-privacy"
                onClick={() => setOpen(false)}
                className="text-base text-zinc-300 hover:text-teal-300 transition-colors"
              >
                Privacy Policy
              </Link>
              <button
                data-testid="mobile-nav-cta-start-project"
                onClick={() => go("contact")}
                className="mt-2 text-sm font-semibold bg-teal-500 text-[#121316] px-5 py-3 rounded-full w-fit"
              >
                Start a Project
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
